import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiSun, FiMoon, FiEye, FiCompass, FiHeart, FiTrendingUp } from "react-icons/fi";

const ARCHETYPES = [
  { name: "The Reflector", trait: "Finds clarity through careful examination of experience", accent: "border-indigo-400/40" },
  { name: "The Healer", trait: "Builds emotional resilience and lasting inner balance", accent: "border-rose-400/40" },
  { name: "The Visionary", trait: "Gains direction through possibility and forward momentum", accent: "border-violet-400/40" },
  { name: "The Guide", trait: "Grows through supporting, advising, and holding space for others", accent: "border-sky-400/40" },
  { name: "The Alchemist", trait: "Transforms through reinvention and deep personal change", accent: "border-amber-400/40" },
  { name: "The Liberator", trait: "Advances through authentic self-expression and truth-telling", accent: "border-teal-400/40" },
];

const BENEFITS = [
  { icon: FiEye, title: "You See Your Patterns", text: "The same argument keeps happening. The same decision keeps being deferred. Reflection surfaces why — so you can change it instead of reliving it.", accent: "border-indigo-400/40", ico: "text-indigo-300" },
  { icon: FiHeart, title: "Emotional Reactivity Drops", text: "When you understand what you are feeling and why, you stop being controlled by it. You gain the pause that changes how you respond to everything.", accent: "border-rose-400/40", ico: "text-rose-300" },
  { icon: FiTrendingUp, title: "Growth Accelerates", text: "Experience without reflection does not teach. Reflection is how experience becomes wisdom — and wisdom is what changes the outcome next time.", accent: "border-violet-400/40", ico: "text-violet-300" },
  { icon: FiCompass, title: "Decisions Get Clearer", text: "When you are clear on your values, priorities stop competing. The right choice becomes obvious. The ones you were agonizing over resolve themselves.", accent: "border-sky-400/40", ico: "text-sky-300" },
  { icon: FiSun, title: "You Stop Surviving, Start Living", text: "Most people are so focused on getting through the day they never ask what the day was for. Reflection creates the space where intention lives.", accent: "border-amber-400/40", ico: "text-amber-300" },
  { icon: FiMoon, title: "You Sleep Differently", text: "The mind that has processed its day does not run loops at 2am. Evening reflection gives your nervous system permission to close the file.", accent: "border-teal-400/40", ico: "text-teal-300" },
];

const ReflectionPage = () => (
  <>
    <Helmet>
      <title>Daily Self-Reflection | Guided Reflection Prompts for Growth | Andlegur</title>
      <meta name="description" content="Learn how daily self-reflection builds emotional intelligence, clarity, and personal growth. Explore guided reflection prompts and tools on Andlegur — free on iOS, Android, and web." />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href="https://andlegur.com/reflection" />
    </Helmet>

    <div className="bg-[#05091B] text-indigo-100 overflow-x-hidden">

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <img src="/web_images/Andlegur%20web%203.webp" alt="" className="w-full h-full object-cover opacity-30 mix-blend-screen" loading="eager" />
          <div className="absolute inset-0 bg-linear-to-t from-[#05091B] via-[#05091B]/50 to-[#05091B]/60" />
          <div className="absolute inset-0 bg-linear-to-r from-[#05091B] via-transparent to-[#05091B]" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center py-32">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-indigo-400 text-sm uppercase tracking-widest mb-5">
            The Practice That Separates Who You Are From Who You Want to Be
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }} className="text-4xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            There is a version of you that already knows what to do.<br />
            <span className="text-indigo-300">Self-reflection is how you hear them.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="text-indigo-200 text-lg sm:text-xl mb-10 max-w-2xl mx-auto leading-relaxed">
            Think about the last time you slowed down long enough to truly understand yourself. Not distract yourself. Not perform for others. Truly understand. That pause is where everything changes.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }} className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/join" className="bg-indigo-600 hover:bg-indigo-500 text-white px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-2xl">Begin Reflecting Today →</Link>
            <Link to="/download" className="bg-white/10 backdrop-blur-sm border border-white/30 hover:bg-white/20 text-white px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300">Get the App</Link>
          </motion.div>
        </div>
      </section>

      {/* Why section */}
      <section className="py-24 border-t border-indigo-800/40">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid sm:grid-cols-2 gap-14 items-center">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ minHeight: "460px" }}>
              <img src="/web_images/Andlegur%20web%2016.webp" alt="The cost of living without self-reflection — why most people stay stuck" className="w-full h-full object-cover absolute inset-0" loading="lazy" />
              <div className="absolute inset-0 bg-linear-to-t from-indigo-950/80 via-transparent to-transparent" />
              <p className="absolute bottom-8 left-8 right-8 text-white text-xl font-light italic leading-snug">
                "The people you admire most — the ones who seem calm, clear, and purposeful — they all share one daily practice."
              </p>
            </div>
            <div>
              <p className="text-indigo-400 text-xs uppercase tracking-widest mb-4">What It Actually Is</p>
              <h2 className="text-3xl sm:text-4xl font-light text-white mb-5 leading-snug">
                Every week you go without reflection<br />
                <span className="text-indigo-300">is a week lived on autopilot — reacting to life instead of authoring it.</span>
              </h2>
              <div className="w-12 h-px bg-indigo-500 mb-7" />
              <p className="text-indigo-200 text-base leading-relaxed mb-5">
                Self-reflection is not navel-gazing — it is a skill. And like any skill, it becomes powerful only when practiced consistently with good structure. Without that, most people's attempts at reflection become rumination: the same thoughts circling without resolution.
              </p>
              <p className="text-indigo-300 text-base leading-relaxed">
                Andlegur gives you a different kind of reflection — a specific question, rooted in your personal archetype, that cuts past the noise and reaches what actually matters today.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefit cards */}
      <section className="py-20 border-t border-indigo-800/40">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-indigo-400 text-xs uppercase tracking-widest mb-3">What Changes When You Reflect</p>
            <h2 className="text-3xl sm:text-4xl font-light text-white mb-4">
              Six things that shift the moment<br /><span className="text-indigo-300">you start reflecting daily.</span>
            </h2>
            <p className="text-indigo-300 text-base max-w-xl mx-auto">Not someday. Most people feel the first shift within a week.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map(({ icon: Icon, title, text, accent, ico }, i) => (
              <motion.div key={title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.07 }} className={"bg-[#0E1833] border " + accent + " rounded-2xl p-7 flex flex-col gap-4"}>
                <div className={"w-10 h-10 rounded-xl border " + accent + " bg-white/5 flex items-center justify-center"}>
                  <Icon className={"w-5 h-5 " + ico} />
                </div>
                <h3 className="text-white font-semibold">{title}</h3>
                <p className="text-indigo-300 text-sm leading-relaxed">{text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Prompts section */}
      <section className="relative py-28 overflow-hidden border-t border-indigo-800/40">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <img src="/web_images/Andlegur%20web%2022.webp" alt="" className="w-full h-full object-cover opacity-15 mix-blend-screen" loading="lazy" />
          <div className="absolute inset-0 bg-linear-to-t from-[#05091B] via-[#05091B]/70 to-[#05091B]" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-indigo-400 text-xs uppercase tracking-widest mb-3">The Questions That Move You</p>
            <h2 className="text-3xl sm:text-4xl font-light text-white leading-snug">
              Not "what are you grateful for."<br />
              <span className="text-indigo-300">Questions that find the thing you have not examined yet.</span>
            </h2>
          </div>
          <div className="space-y-4">
            {[
              "What am I avoiding right now — and what is the fear underneath that avoidance?",
              "Where in my life am I performing versus genuinely showing up?",
              "What would I do differently today if I trusted myself completely?",
              "What feeling have I been carrying this week that I have never named?",
              "The belief about myself that is no longer true — what is it?",
            ].map((p) => (
              <div key={p} className="flex items-start gap-3 bg-indigo-900/30 border border-indigo-700/40 rounded-xl px-5 py-3">
                <span className="text-indigo-400 mt-0.5 shrink-0">→</span>
                <p className="text-indigo-200 text-sm italic leading-relaxed">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Morning vs Evening */}
      <section className="py-20 border-t border-indigo-800/40">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-indigo-400 text-xs uppercase tracking-widest mb-3">When to Reflect</p>
            <h2 className="text-3xl sm:text-4xl font-light text-white">Two moments.<br /><span className="text-indigo-300">Both transformative.</span></h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-8">
            <div className="bg-[#0E1833] border border-amber-400/30 rounded-2xl overflow-hidden flex flex-col group">
              <div className="px-8 pt-9 pb-8 flex flex-col gap-4 flex-1">
                <div className="flex items-center gap-3">
                  <FiSun className="w-6 h-6 text-amber-400" />
                  <p className="text-indigo-400 text-xs uppercase tracking-widest">Morning Practice</p>
                </div>
                <h3 className="text-white text-xl font-light leading-snug">Set the intention before the day sets you.</h3>
                <div className="w-10 h-px bg-amber-500/40" />
                <p className="text-indigo-300 text-sm leading-relaxed">Five minutes in the morning, before the noise begins, changes the entire quality of what follows. A morning reflection on Andlegur does not just organize your thoughts — it establishes how you want to show up. That distinction determines everything.</p>
              </div>
              <div className="relative h-52 overflow-hidden shrink-0">
                <img src="/web_images/Andlegur%20web%204.webp" alt="Morning reflection — starting the day with intention on Andlegur" className="w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-linear-to-t from-[#0E1833]/60 via-transparent to-transparent" />
              </div>
            </div>
            <div className="bg-[#0E1833] border border-indigo-400/30 rounded-2xl overflow-hidden flex flex-col group">
              <div className="px-8 pt-9 pb-8 flex flex-col gap-4 flex-1">
                <div className="flex items-center gap-3">
                  <FiMoon className="w-6 h-6 text-indigo-300" />
                  <p className="text-indigo-400 text-xs uppercase tracking-widest">Evening Practice</p>
                </div>
                <h3 className="text-white text-xl font-light leading-snug">Process the day before it becomes tomorrow's weight.</h3>
                <div className="w-10 h-px bg-indigo-500/40" />
                <p className="text-indigo-300 text-sm leading-relaxed">The experiences you do not process become patterns you live inside. Evening reflection gives your nervous system permission to close the day — to integrate what happened and release what does not need to be carried forward.</p>
              </div>
              <div className="relative h-52 overflow-hidden shrink-0">
                <img src="/web_images/Andlegur%20web%206.webp" alt="Evening reflection — integrating the day for deeper rest on Andlegur" className="w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-linear-to-t from-[#0E1833]/60 via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Archetypes section */}
      <section className="py-24 border-t border-indigo-800/40">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid sm:grid-cols-2 gap-14 items-center">
            <div>
              <p className="text-indigo-400 text-xs uppercase tracking-widest mb-4">What Makes Andlegur Different</p>
              <h2 className="text-3xl sm:text-4xl font-light text-white mb-5 leading-snug">
                You do not grow the same way as everyone else.<br />
                <span className="text-indigo-300">So why would your reflection practice be identical?</span>
              </h2>
              <div className="w-12 h-px bg-indigo-500 mb-7" />
              <p className="text-indigo-200 text-base leading-relaxed mb-8">
                Andlegur's six growth archetypes are not personality labels — they are guidance maps. Discover yours in onboarding, and every reflection prompt, insight, and practice you receive will be calibrated to the exact kind of growth that actually works for who you are.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {ARCHETYPES.map(({ name, trait, accent }) => (
                  <div key={name} className={"bg-indigo-900/30 border " + accent + " rounded-xl px-4 py-3"}>
                    <p className="text-white text-sm font-semibold mb-1">{name}</p>
                    <p className="text-indigo-400 text-xs leading-relaxed">{trait}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ minHeight: "500px" }}>
              <img src="/web_images/Andlegur%20web%2027.webp" alt="Archetype-based reflection on Andlegur — personalized to how you actually grow" className="w-full h-full object-cover absolute inset-0" loading="lazy" />
              <div className="absolute inset-0 bg-linear-to-t from-indigo-950/80 via-transparent to-transparent" />
              <p className="absolute bottom-8 left-8 right-8 text-white text-xl font-light italic">
                "Five minutes of honest reflection today is worth more than a year of avoidance."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32 overflow-hidden border-t border-indigo-800/40">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <img src="/web_images/Andlegur%20web%2036.webp" alt="" className="w-full h-full object-cover opacity-20 mix-blend-screen" loading="lazy" />
          <div className="absolute inset-0 bg-linear-to-t from-[#05091B] via-[#05091B]/60 to-[#05091B]" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-5xl font-light text-white mb-5 leading-tight">
            The clarity you are looking for<br /><span className="text-indigo-300">is on the other side of five quiet minutes.</span>
          </h2>
          <p className="text-indigo-300 text-lg leading-relaxed mb-10">
            Discover your archetype. Receive your first personalized reflection prompt. Experience what it feels like to finally understand what has been quietly driving you. Free — iOS, Android, and web.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/join" className="bg-indigo-600 hover:bg-indigo-500 text-white px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-2xl">Discover Your Archetype →</Link>
            <Link to="/download" className="bg-white/10 backdrop-blur-sm border border-white/30 hover:bg-white/20 text-white px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300">Download Free</Link>
          </div>
        </div>
      </section>

    </div>
  </>
);

export default ReflectionPage;
